import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';
import { SearchInput, Title } from '@components/v2/ui';
import { Text } from '@components/v2/ui/Text';
import { NOT_APPLICABLE } from '@config/constants/common';
import { TSite } from '@global-types/index';
import {
	Accordion,
	ActionIcon,
	Avatar,
	Divider,
	Flex,
	Group,
	Image,
	Stack,
} from '@mantine/core';
import {
	IconPlus,
	IconQuestionMark,
} from '@tabler/icons-react';
import { getRatioLabel } from '@utils/functions';
import { useTranslation } from 'react-i18next';
import classes from './SitesByProject.module.css';

export type MeterpointData = {
	serialNumber: string;
	countryCode: string;
	place: string;
	unit: string;
};

export type ConversionFactorData = {
	startDate: string;
	unit: string;
	countryCode: string;
	place: string;
	ratio: string;
};

export type TSiteCustom = {
	id: string;
	name: string;
	countryCode: string;
	place: string;
	unit: string;
	site: TSite;
	meterPoints: MeterpointData[];
	conversionFactors: ConversionFactorData[];
};

export const SitesByProject = ({
	data = [],
	onSearchSite,
}: {
	data?: TSiteCustom[];
	onSearchSite: (siteName: string) => void;
}) => {
	const { t } = useTranslation();
	const defaultValue =
		data && data.length <= 2
			? data.map((site) => site.id)
			: [];

	return (
		<Flex direction="column" flex={1} gap="16px">
			<Flex
				justify={'space-between'}
				align={'center'}
				mb="24px"
			>
				<Title
					order={5}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{t('sidebar.sites')}
				</Title>
				<Group gap={'var(--mantine-spacing-xs)'}>
					<SearchInput
						onChange={onSearchSite}
						placeholder={t('global.search')}
					/>
				</Group>
			</Flex>
			{data && Array.isArray(data) && data.length > 0 && (
				<Accordion
					classNames={classes}
					variant="separated"
					w="100%"
					radius="25px"
					multiple
					defaultValue={defaultValue}
				>
					{data.map((site) => (
						<Accordion.Item key={site.id} value={site.id}>
							<Accordion.Control>
								{site.name}
							</Accordion.Control>
							<Accordion.Panel>
								<Flex wrap="wrap">
									<Flex
										direction="column"
										flex={1}
										miw={250}
										mih={100}
										pl="48px"
										pr="48px"
										style={{
											borderRight: '1px solid #D4DDE7',
										}}
									>
										<Flex h="48px" align="center">
											<Text
												variant="button"
												c="var(--mantine-color-igluPrimary-9)"
											>
												{t('offsets.stepper.step1')}
											</Text>
										</Flex>
										<div>
											<Flex
												justify="start"
												align="center"
											>
												<Flex miw="100px" flex={1}>
													<Text
														variant="body2"
														c="var(--mantine-color-igluSecondary-9)"
													>
														{t('global.location')}
													</Text>
												</Flex>
												<Flex gap="5px" flex={2}>
													<Avatar
														radius="xl"
														src={`https://flagcdn.com/w40/${site?.countryCode?.toLowerCase()}.png`}
														size={18}
													>
														<IconQuestionMark size={18} />
													</Avatar>
													<Text
														variant="body2"
														c="var(--mantine-color-igluTextPrimary-9)"
													>
														{site?.place ??
															NOT_APPLICABLE}
													</Text>
												</Flex>
											</Flex>
											<Flex
												justify="start"
												align="center"
											>
												<Flex miw="100px" flex={1}>
													<Text
														variant="body2"
														c="var(--mantine-color-igluSecondary-9)"
													>
														{t('global.unit')}
													</Text>
												</Flex>
												<Flex flex={2}>
													<Text
														variant="body2"
														c="var(--mantine-color-igluTextPrimary-9)"
													>
														{site?.unit ?? NOT_APPLICABLE}
													</Text>
												</Flex>
											</Flex>
										</div>
									</Flex>
									<Flex
										direction="column"
										flex={1}
										miw={250}
										mih={100}
										pl="48px"
										pr="48px"
										justify="space-around"
										style={{
											borderRight: '1px solid #D4DDE7',
										}}
									>
										<Flex
											align="center"
											justify="space-between"
										>
											<Text
												variant="button"
												c="var(--mantine-color-igluPrimary-9)"
											>
												{t('sidebar.conversionFactors')}
											</Text>
											<ActionIcon
												variant="outline"
												radius="xl"
												size="48px"
												color="var(--mantine-color-igluBorder-9)"
											>
												<IconPlus
													style={{
														width: '70%',
														height: '70%',
													}}
													stroke={1.5}
													color="var(--mantine-color-igluPrimary-9)"
												/>
											</ActionIcon>
										</Flex>
										{site?.conversionFactors &&
											Array.isArray(
												site.conversionFactors
											) &&
											site.conversionFactors.map(
												(conversionFactor, index) => (
													<Flex
														direction="column"
														key={index}
													>
														<Flex
															justify="start"
															align="center"
														>
															<Flex flex={1} miw="100px">
																<Text
																	variant="body2"
																	c="var(--mantine-color-igluSecondary-9)"
																>
																	{t('global.startDate')}
																</Text>
															</Flex>
															<Flex flex={2}>
																<Text
																	variant="body2"
																	c="var(--mantine-color-igluTextPrimary-9)"
																>
																	{conversionFactor?.startDate ??
																		NOT_APPLICABLE}
																</Text>
															</Flex>
														</Flex>
														<Flex
															justify="start"
															align="center"
														>
															<Flex flex={1} miw="100px">
																<Text
																	variant="body2"
																	c="var(--mantine-color-igluSecondary-9)"
																>
																	{t('global.value')}
																</Text>
															</Flex>
															<Flex flex={2}>
																<Text
																	variant="body2"
																	c="var(--mantine-color-igluTextPrimary-9)"
																>
																	{getRatioLabel(
																		Number(
																			conversionFactor.ratio
																		),
																		conversionFactor.unit.toLocaleUpperCase()
																	)}
																</Text>
															</Flex>
														</Flex>
														{index !=
															site.conversionFactors
																.length -
																1 && (
															<Divider
																mt="5px"
																mb="5px"
															/>
														)}
													</Flex>
												)
											)}
									</Flex>
									<Flex
										direction="column"
										flex={1}
										miw={250}
										mih={100}
										pl="48px"
										pr="48px"
									>
										<Flex
											h="48px"
											align="center"
											justify="space-between"
										>
											<Text
												variant="button"
												c="var(--mantine-color-igluPrimary-9)"
											>
												{t('global.meterPoints')}
											</Text>
											<ActionIcon
												variant="outline"
												radius="xl"
												size="48px"
												color="var(--mantine-color-igluBorder-9)"
											>
												<IconPlus
													style={{
														width: '70%',
														height: '70%',
													}}
													stroke={1.5}
													color="var(--mantine-color-igluPrimary-9)"
												/>
											</ActionIcon>
										</Flex>
										{site?.meterPoints &&
											Array.isArray(site.meterPoints) &&
											site.meterPoints.map(
												(meterPoint, index) => (
													<Flex
														direction="column"
														key={index}
													>
														<Flex
															justify="start"
															align="center"
														>
															<Flex flex={1} miw="100px">
																<Text
																	variant="body2"
																	c="var(--mantine-color-igluSecondary-9)"
																>
																	{t(
																		'global.serialNumber'
																	)}
																</Text>
															</Flex>
															<Flex flex={2}>
																<Text
																	variant="body2"
																	c="var(--mantine-color-igluTextPrimary-9)"
																>
																	{meterPoint?.serialNumber ??
																		NOT_APPLICABLE}
																</Text>
															</Flex>
														</Flex>
														<Flex
															justify="start"
															align="center"
														>
															<Flex flex={1} miw="100px">
																<Text
																	variant="body2"
																	c="var(--mantine-color-igluSecondary-9)"
																>
																	{t('global.location')}
																</Text>
															</Flex>
															<Flex gap="5px" flex={2}>
																<Avatar
																	radius="xl"
																	src={`https://flagcdn.com/w40/${meterPoint?.countryCode?.toLowerCase()}.png`}
																	size={18}
																>
																	<IconQuestionMark
																		size={18}
																	/>
																</Avatar>
																<Text
																	variant="body2"
																	c="var(--mantine-color-igluTextPrimary-9)"
																>
																	{meterPoint?.place ??
																		NOT_APPLICABLE}
																</Text>
															</Flex>
														</Flex>
														<Flex
															justify="start"
															align="center"
														>
															<Flex flex={1} miw="100px">
																<Text
																	variant="body2"
																	c="var(--mantine-color-igluSecondary-9)"
																>
																	{t('global.unit')}
																</Text>
															</Flex>
															<Flex flex={2}>
																<Text
																	variant="body2"
																	c="var(--mantine-color-igluTextPrimary-9)"
																>
																	{meterPoint?.unit ??
																		NOT_APPLICABLE}
																</Text>
															</Flex>
														</Flex>
														{index !=
															site.meterPoints.length -
																1 && (
															<Divider
																mt="5px"
																mb="5px"
															/>
														)}
													</Flex>
												)
											)}
									</Flex>
								</Flex>
							</Accordion.Panel>
						</Accordion.Item>
					))}
				</Accordion>
			)}
			{data &&
				Array.isArray(data) &&
				data.length == 0 && (
					<Stack
						justify="center"
						align="center"
						pt="15px"
						className={classes.containerNoDatas}
					>
						<Image
							src={NoRecordsIcon}
							className={classes.iconNoRecords}
						/>
						<Text
							className={classes.descriptionNoRecords}
						>
							{t('global.noSitesAdded')}
						</Text>
					</Stack>
				)}
		</Flex>
	);
};
